import { useState } from "react"
import React from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { selectGameConfig } from "src/store/common"
import { selectGamePlayers } from "src/store/game"
import ModalService from "src/modals"

import UnitItem from "src/components/UnitItem"
import { roundTotals, subRoundRow } from "src/utils/battleTable"

/* One battle round, told sub-round by sub-round: a navigator over the engine's trace, each stop
   drawing the very board that sub-round fielded — one unit type per slot, that sub-round's deaths
   pinned to their cells, so the badges always add up — and the numeric table underneath for
   whoever wants the raw figures. Shared by the live battle view and the finished report: the same
   round must look the same wherever it is read. */

const ROW_LAYOUT = {
  rear: { corners: "special", middle: "back" },
  line: { corners: "flanks", middle: "front" },
}

const BattleRoundView = ({ detail, roundNumber = null, headerActionFor = null }) => {
  const { t } = useTranslation()
  const players = useSelector(selectGamePlayers)
  const gameConfig = useSelector(selectGameConfig)
  const rules = gameConfig?.rules_config || {}
  const subRounds = detail?.sub_rounds || []
  const hasSubBoards = subRounds.length > 0 && Object.values(subRounds[0])[0]?.board != null
  // Latest sub-round first: "where did it end up" is the common question.
  const [subIndex, setSubIndex] = useState(Math.max(0, subRounds.length - 1))

  if (!detail) return null

  const teamOfId = (id) => players.find((p) => p.user.id === Number(id))?.team
  const shownIndex = Math.min(subIndex, Math.max(0, subRounds.length - 1))

  // The stats each side actually fought with, research and race included — the server ships them
  // on the event so the tooltip never quotes a base number the engine did not use.
  const statsForTeamUnit = (teamKey, unit) => {
    for (const [id, units] of Object.entries(detail.stats_by_unit || {})) {
      if (teamOfId(id) === teamKey && units?.[unit]) return units[unit]
    }
    return null
  }
  const statsTitle = (teamKey, unit) => {
    const stats = statsForTeamUnit(teamKey, unit)
    if (!stats) return undefined
    return t("game.battle.unitStats", { attack: stats.ATTACK, defense: stats.DEFENSE, health: stats.HEALTH })
  }

  // The wall the middle line stands for: the shown sub-round's remaining absorption when the
  // trace carries it (new events), else the event's post-round state, else nothing to say.
  const wallForShown = () => {
    const entry = subRounds[shownIndex] || {}
    const traced = Object.values(entry)
      .map((row) => row?.wall_hp)
      .find((wall) => wall != null)
    if (traced) return traced
    return Object.values(detail.wall_state || {}).find((wall) => wall != null) || null
  }

  const boardRows = (zoneSlots, mirrored, slotLosses = null, teamKey = null) => {
    const slotBoxOf = (zone, index) => {
      // A slot holds a list of stacks now: the board belongs to the whole side, so allies' units
      // — different types included — share seats (2026-08-21).
      const stacks = (zoneSlots?.[zone] || [])[index] || []
      const lost = slotLosses?.[zone]?.[index] ?? 0
      return (
        <div
          className={`battle-slot zone-${zone}${stacks.length ? "" : " empty"}`}
          key={`${zone}-${index}`}
          title={t("game.zoneHelp.open", { zone: t(`game.battle.zones.${zone}`) })}
          onClick={() => ModalService.show("ZoneHelpModal", { zone })}
        >
          <span className="battle-slot-tag">{t(`game.battle.zoneShort.${zone}`)}</span>
          {stacks.length > 0 ? (
            <div className="battle-slot-stack">
              {stacks.map(([unit, count], stackIndex) => (
                <span key={`${unit}-${stackIndex}`} title={statsTitle(teamKey, unit)}>
                  <UnitItem type={unit} count={count} showLabel />
                </span>
              ))}
              {lost > 0 && <span className="battle-unit-lost">-{lost}</span>}
            </div>
          ) : (
            <span className="battle-slot-ghost">{t(`game.battle.zoneShort.${zone}`)}</span>
          )}
        </div>
      )
    }
    const rowOf = (layout) => {
      const corners = rules.BATTLEFIELD_ZONE_SLOTS?.[layout.corners] ?? 0
      const middle = rules.BATTLEFIELD_ZONE_SLOTS?.[layout.middle] ?? 0
      return (
        <div className="battle-row">
          {slotBoxOf(layout.corners, 0)}
          {Array.from({ length: middle }, (_, i) => slotBoxOf(layout.middle, i))}
          {corners > 1 ? slotBoxOf(layout.corners, 1) : <div className="battle-slot spacer" />}
        </div>
      )
    }
    return mirrored
      ? [<div key="line">{rowOf(ROW_LAYOUT.line)}</div>, <div key="rear">{rowOf(ROW_LAYOUT.rear)}</div>]
      : [<div key="rear">{rowOf(ROW_LAYOUT.rear)}</div>, <div key="line">{rowOf(ROW_LAYOUT.line)}</div>]
  }

  // One board per side. The players holding that side are named on its header — the board is
  // theirs together (2026-08-21), so there is one field per team, not one per ally.
  const sideBlocks = (boardOf, lossesOf, extraOf, mirroredFor) => {
    const participants = [
      ...new Set(subRounds[shownIndex] ? Object.keys(subRounds[shownIndex]) : Object.keys(detail.committed_by_unit || {})),
    ]
    const byTeam = { human: [], zombie: [] }
    participants.forEach((id) => {
      const player = players.find((p) => p.user.id === Number(id))
      if (player && byTeam[player.team]) byTeam[player.team].push({ id, player })
    })
    const block = (teamKey) => {
      const board = boardOf(teamKey, byTeam[teamKey])
      if (!board || byTeam[teamKey].length === 0) return null
      return (
        <section className={`battle-side ${teamKey}`} key={teamKey}>
          <div className="battle-history-player">
            <header className="battle-side-head">
              <span className="battle-side-team">{t(`game.battle.${teamKey}`)}</span>
              <span className="battle-side-owners">
                {byTeam[teamKey].map(({ player }) => player.user.username).join(" · ")}
              </span>
              {headerActionFor?.(teamKey, byTeam[teamKey][0]?.id)}
              {extraOf(teamKey, byTeam[teamKey])}
            </header>
            {boardRows(board, mirroredFor(teamKey), lossesOf(teamKey), teamKey)}
          </div>
        </section>
      )
    }
    // The middle line *is* the wall when the defender has one: it carries the remaining
    // absorption for the shown sub-round, and says so when it has fallen — the moment the horde
    // pours through should be readable off the line, not deduced from the damage columns.
    const wall = wallForShown()
    const wallLine = wall ? (
      <div
        className={`battle-line wall${wall.hp_left <= 0 ? " down" : ""}`}
        role="img"
        aria-label={t("game.battle.wallHp", { left: wall.hp_left, max: wall.hp_max })}
      >
        <div
          className="battle-line-fill"
          style={{ width: `${wall.hp_max > 0 ? Math.round((wall.hp_left / wall.hp_max) * 100) : 0}%` }}
        />
        <span className="battle-line-text">
          🧱{" "}
          {wall.hp_left <= 0
            ? t("game.battle.wallDown")
            : t("game.battle.wallHp", { left: wall.hp_left, max: wall.hp_max })}
        </span>
      </div>
    ) : (
      <div className="battle-line" aria-hidden="true" />
    )
    /* One wrapper around both boards and the line between them: the field is read as a whole —
       the front faces the bar, the flanks are the corners — so the two sides are laid out
       together and shrink together on a narrow screen rather than being told apart. */
    return (
      <div className="battle-field">
        {block("human")}
        {wallLine}
        {block("zombie")}
      </div>
    )
  }

  const subRoundBoards = () => {
    const entry = subRounds[shownIndex] || {}
    const rowFor = (members) => entry[members[0]?.id]
    return sideBlocks(
      (_teamKey, members) => rowFor(members)?.board,
      (teamKey) => Object.values(entry).find((row) => row.team === teamKey)?.losses_by_slot,
      (_teamKey, members) => {
        const row = rowFor(members)
        if (!row) return null
        const engaged = Object.values(entry)
          .filter((other) => other.team === row.team)
          .reduce((total, other) => total + Number(other.engaged || 0), 0)
        const reserve = Object.values(entry)
          .filter((other) => other.team === row.team)
          .reduce((total, other) => total + Number(other.reserve || 0), 0)
        const capacity = detail.battlefield_capacity?.[row.team]
        return (
          <span className="battle-side-total">
            {t("game.battle.onTheField", { count: engaged, capacity: capacity ?? "—" })}
            {reserve > 0 ? ` · ${t("game.battle.reserves", { count: reserve })}` : ""}
          </span>
        )
      },
      (teamKey) => teamKey === "zombie"
    )
  }

  const roundBoardsFallback = () =>
    sideBlocks(
      (teamKey) => detail.boards?.[teamKey],
      (teamKey) => detail.losses_by_slot?.[teamKey],
      () => null,
      (teamKey) => teamKey === "zombie"
    )

  // ---- the numeric table, always on ----
  const teams = ["human", "zombie"].filter((teamKey) =>
    Object.keys(detail.losses_by_unit || {}).some((id) => teamOfId(id) === teamKey) ||
    Object.keys(detail.committed_by_unit || {}).some((id) => teamOfId(id) === teamKey)
  )
  // The columns' arithmetic lives in `utils/battleTable` (what the wall soaks is *blocked*, the
  // round has a total line), where it is tested against a real trace.
  const teamRow = (entry, teamKey) => subRoundRow(entry, teamKey, teamOfId)
  const cells = (row, key) => (
    <React.Fragment key={key}>
      <td>{row.engaged}</td>
      <td>{row.dealt}</td>
      <td>{row.taken}</td>
      {/* The wall's share is named: "blocked 46 872" over a wall that just fell is the
          number, and the glyph says who ate it. */}
      <td title={row.wall > 0 ? t("game.battle.srWallTitle", { amount: row.wall }) : undefined}>
        {row.blocked}
        {row.wall > 0 && <span className="sr-wall" aria-hidden="true"> 🧱</span>}
      </td>
      <td className={`sr-divider${row.deaths > 0 ? " deaths" : ""}`}>{row.deaths}</td>
    </React.Fragment>
  )
  const subRoundTable = () => (
    <>
      {/* The same cue the board gets, for the same reason: at 390px only the human half of this
          table is on screen and the enemy's columns are past the right edge with nothing to say
          they exist. Hidden wherever the table fits (BattleModal.css). */}
      <p className="battle-scroll-hint for-table">{t("game.battle.scrollHintTable")}</p>
    <div className="battle-subrounds-table">
      <table>
        <thead>
          <tr>
            <th></th>
            {teams.map((teamKey) => (
              <th key={teamKey} colSpan={5} className={`sr-team ${teamKey}`}>
                {t(`game.battle.${teamKey}`)}
              </th>
            ))}
          </tr>
          <tr>
            <th>{t("game.battle.srColumn")}</th>
            {teams.map((teamKey) => (
              <React.Fragment key={teamKey}>
                <th>{t("game.battle.srEngaged")}</th>
                <th>{t("game.battle.srDealt")}</th>
                <th>{t("game.battle.srTaken")}</th>
                <th>{t("game.battle.srBlocked")}</th>
                <th className="sr-divider">{t("game.battle.srDeaths")}</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {subRounds.map((entry, index) => (
            <tr
              key={index}
              className={hasSubBoards && index === shownIndex ? "sr-current" : ""}
              onClick={hasSubBoards ? () => setSubIndex(index) : undefined}
            >
              <td>{index + 1}</td>
              {teams.map((teamKey) => cells(teamRow(entry, teamKey), teamKey))}
            </tr>
          ))}
        </tbody>
        {/* The round, summed: every row above is one sub-round of ten, and a reader adding them
            up by hand concluded that thirty-five tanks "only did 64k" (owner, 2026-09-16). */}
        {subRounds.length > 1 && (
          <tfoot>
            <tr className="sr-total">
              <td>{t("game.battle.srTotal")}</td>
              {teams.map((teamKey) => cells(roundTotals(subRounds, teamKey, teamOfId), teamKey))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
    </>
  )

  return (
    <div className="battle-round-view">
      {hasSubBoards ? (
        <>
          <div className="battle-subround-nav">
            <button
              type="button"
              className="round-nav-arrow"
              onClick={() => setSubIndex(Math.max(0, shownIndex - 1))}
              disabled={shownIndex <= 0}
              aria-label={t("game.roundReport.older")}
            >
              ‹
            </button>
            <span className="battle-subround-label">
              {roundNumber != null ? `${t("game.battle.round", { count: roundNumber })} · ` : ""}
              {t("game.battle.subRoundNav", { current: shownIndex + 1, total: subRounds.length })}
            </span>
            <button
              type="button"
              className="round-nav-arrow"
              onClick={() => setSubIndex(Math.min(subRounds.length - 1, shownIndex + 1))}
              disabled={shownIndex >= subRounds.length - 1}
              aria-label={t("game.roundReport.newer")}
            >
              ›
            </button>
          </div>
          {subRoundBoards()}
        </>
      ) : (
        roundBoardsFallback()
      )}
      {subRounds.length > 0 && subRoundTable()}
    </div>
  )
}

export default BattleRoundView
